import { RegionExtraData } from '@/editor/data/region/Region';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { worldPosToClientPos } from '@/editor/utils';
import { getRegionCentroid } from '@/editor/utils/region';
import { getAreaInfo } from '@/editor/utils/room/area';
import styles from './textInfo.module.scss';

export interface ITextInfoConstructor<T> {
  data: T;
  name: string;
  visible: boolean;
}

export class TextInfo<T extends RegionExtraData> {
  protected _element: HTMLElement;
  protected _nameEl: HTMLElement;
  protected _areaEl: HTMLElement;
  private _name: string;
  private _area: number = 0;
  /** 不含偏移的屏幕基准位置（标签中心） */
  protected _position: IPoint2d = { x: 0, y: 0 };
  private _worldPosition: IPoint2d = { x: 0, y: 0 };
  private _positionOffset: IPoint2d = { x: 0, y: 0 };

  constructor({ data, name, visible }: ITextInfoConstructor<T>) {
    this._name = name;
    this._element = document.createElement('div');
    this._element.classList.add(styles.textInfo);
    this._nameEl = document.createElement('div');
    this._areaEl = document.createElement('div');
    this._element.appendChild(this._nameEl);
    this._element.appendChild(this._areaEl);
    this._elContainer.appendChild(this._element);
    this.update({ data, name, visible });
  }

  get nameEl() {
    return this._nameEl;
  }

  get areaEl() {
    return this._areaEl;
  }

  private get _elContainer() {
    return globalStores.editorStateStore.textInfoContainer;
  }

  update({ data, name, visible }: ITextInfoConstructor<T>) {
    this._name = name;
    this._area = getAreaInfo(data).coreArea;
    this._worldPosition = getRegionCentroid(data);
    this._position = this._computeBrowserPosition();

    this._nameEl.textContent = this._name;
    this._areaEl.textContent = `${this._area.toFixed(2)} m²`;

    const shouldHide = this._checkOutsideViewport();
    if (!shouldHide) {
      this._updateTransform();
    }
    this._setElementVisible(!shouldHide);
    this.setVisible(visible);
  }

  public updatePosition() {
    this._position = this._computeBrowserPosition();
    const shouldHide = this._checkOutsideViewport();
    if (!shouldHide) {
      this._updateTransform();
    }
    this._setElementVisible(!shouldHide);
  }

  public setPositionOffset(offset?: IPoint2d) {
    const nextOffset = offset ? { ...offset } : { x: 0, y: 0 };
    const changed = this._positionOffset.x !== nextOffset.x || this._positionOffset.y !== nextOffset.y;
    this._positionOffset = nextOffset;
    return changed;
  }

  public getBoundingClientRect() {
    return this._element.getBoundingClientRect();
  }

  private _setElementVisible(visible: boolean) {
    const display = visible ? '' : 'none';
    if (this._element.style.display !== display) {
      this._element.style.display = display;
    }
  }

  /** 计算标签在浏览器显示的位置 */
  private _computeBrowserPosition(): IPoint2d {
    const {
      renderer,
      scene2d: { camera }
    } = globalStores.editorStateStore.sceneInfo;
    return worldPosToClientPos(this._worldPosition, camera, renderer);
  }

  /** 更新标签位置 */
  private _updateTransform(): void {
    const position = this._getDisplayPosition();
    this._element.style.transform = `translate(${position.x}px, ${position.y}px) translate(-50%, -50%)`;
  }

  private _getDisplayPosition(): IPoint2d {
    return {
      x: this._position.x + this._positionOffset.x,
      y: this._position.y + this._positionOffset.y
    };
  }

  private _getEstimatedRect(): IPoint2d[] {
    const labelHeight = 44;
    const labelWidth = Math.max(this._name.length * 12, `${this._area.toFixed(2)} m²`.length * 10);
    const position = this._getDisplayPosition();

    return [
      { x: position.x - labelWidth / 2, y: position.y - labelHeight / 2 },
      { x: position.x + labelWidth / 2, y: position.y - labelHeight / 2 },
      { x: position.x + labelWidth / 2, y: position.y + labelHeight / 2 },
      { x: position.x - labelWidth / 2, y: position.y + labelHeight / 2 }
    ];
  }

  /** 判断文本信息是否完全在视口之外 */
  private _checkOutsideViewport(): boolean {
    const { innerWidth, innerHeight } = window;
    const rect = this._getEstimatedRect();
    if (rect.some((point) => point.x > 0 && point.x < innerWidth && point.y > 0 && point.y < innerHeight)) {
      return false;
    }
    return true;
  }

  updateInfoEnable(enable: boolean) {
    this._element.setAttribute('data-enabled', enable ? 'true' : 'false');
  }

  resetFocus() {
    this._element.removeAttribute('data-enabled');
  }

  setHover(hover: boolean) {
    this._element.setAttribute('data-hover', hover ? 'true' : 'false');
  }

  setSelected(selected: boolean) {
    this._element.setAttribute('data-selected', selected ? 'true' : 'false');
  }

  setVisible(visible: boolean) {
    if (this._element.hidden !== !visible) {
      this._element.hidden = !visible;
    }
  }

  dispose(): void {
    if (this._element && this._element.parentNode) {
      this._element.parentNode.removeChild(this._element);
    }
  }
}
